import './styles/promotions.scss';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from 'gatsby';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';

import { Pagination, TitleBanner } from '../components/Primitives';
import { PromoAccessories, PromoApplianceSingle } from '../components/Listing';
import { ICurrentLocal } from '../interfaces/interfaces';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';

import { useStore } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';

import { IStoreContextInternalAPI } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store/StoreContext';

export const query = graphql`
    query {
        allProductsJson(
            filter: {
                #category: { regex: "^/Drolet/Heaters/" }
                status: { eq: "actif" }
            }
            sort: { attributes: { size: { values: { en_CA: DESC } } } }
        ) {
            nodes {
                id
                code
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    PWMaSoi {
                        group
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    Surfi {
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    EPA {
                        group
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    certifications {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    size {
                        values {
                            en_CA
                            en_US
                            fr_CA
                            id
                        }
                        group
                        index
                    }
                }
                images
                fields {
                    ...productsFieldsImagesFragment
                }
            }
        }
        promoCMS: allProductsJson(
            filter: { code: { eq: "drolet_promotions" } }
        ) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        promotions: allProductsJson(
            filter: {
                category: { regex: "/Drolet/CMS/Promotion/" }
                status: { eq: "actif" }
            }
        ) {
            nodes {
                id
                code
                description {
                    valuesMarkdownRemark {
                        en_CA
                        en_US
                        fr_CA
                    }
                    en_CA
                    en_US
                    fr_CA
                }
                options {
                    code
                    category
                    title {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    available_country {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    start_date {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    end_date {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    expiration_date {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    promotion_condition {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    claimable_product {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    product_code {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    bouttons {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    link {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Promotions({ data }) {
    const { currentLocale } = usePageLocalization();
    const { pricesAndInventoriesCache } =
        useStore() as IStoreContextInternalAPI;
    const [productWithDiscount, setProductWithDiscount] = useState<any[]>();
    const promoCMS = data?.promoCMS?.nodes[0];
    const { t } = useTranslation('pages');
    const promoAttributes = promoCMS?.attributes;
    const metadata = {
        title: promoAttributes?.meta_title?.values[0][currentLocale],
        description:
            promoAttributes?.meta_description?.values[0][currentLocale],
    };
    const [page, setPage] = useState(1);
    const isMobile = useMobileDetect().isMobile();
    const APPLIANCE_PER_PAGE = isMobile ? 6 : 12;
    const responsiveImages = promoCMS?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop?.[0]?.childImageSharp
            ?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile?.[0]?.childImageSharp
            ?.gatsbyImageData;

    const allProducts = data?.allProductsJson?.nodes || [];
    const heaters = allProducts.filter((prod: any) =>
        /^\/Drolet\/Heaters\//.test(prod?.category)
    );

    const stringDateToDate = (dateString: string) => {
        const [day, month, year] = dateString.split('/');
        return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const currentDate = new Date();

    const promoToClaim = data?.promotions?.nodes
        .filter(
            (promo) =>
                promo.code !== 'drolet_promo-brouillon' &&
                stringDateToDate(
                    promo.attributes?.start_date?.values[0][currentLocale]
                ) <= currentDate &&
                stringDateToDate(
                    promo.attributes?.expiration_date?.values[0][currentLocale]
                ) >= currentDate
        )
        .sort(
            (a: any, b: any) =>
                stringDateToDate(
                    b.attributes?.end_date?.values[0][currentLocale]
                ) -
                stringDateToDate(
                    a.attributes?.end_date?.values[0][currentLocale]
                )
        );

    useEffect(() => {
        const skus = heaters.map(
            (product: any) => product?.code ?? ('no-code-found' as string)
        );

        const productRessources = skus.map((sku: any) => {
            return {
                price: pricesAndInventoriesCache.getProductResource(sku),
                sku: sku,
            };
        });

        const heaterSkusWithDiscount = productRessources
            .map((productResource: any) => {
                return {
                    price: productResource?.price?.getPriceImmediate(),
                    sku: productResource.sku,
                };
            })
            .filter(
                (productRessource: any) => productRessource?.price?.discount
            )
            .map((productRessource: any) => productRessource.sku);

        setProductWithDiscount(
            heaters.filter((heater: any) =>
                heaterSkusWithDiscount.includes(heater.code)
            )
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <TitleBanner
                className='titlebanner'
                title={promoCMS?.title?.[currentLocale]}
                imageData={isMobile ? mobileImage : desktopImage}
            />
            <div className='container'>
                <div className='promotions'>
                    <div className='buy-promo section'>
                        <h2>
                            {
                                promoCMS?.attributes?.texte_1?.values?.[0]?.[
                                    currentLocale
                                ]
                            }
                        </h2>
                        <div className='line' />
                        <div className='discounted-products'>
                            {productWithDiscount &&
                            productWithDiscount.length > 0 ? (
                                productWithDiscount
                                    ?.slice(
                                        APPLIANCE_PER_PAGE * (page - 1),
                                        APPLIANCE_PER_PAGE * page
                                    )
                                    ?.map((appliance: any, index: number) => {
                                        return (
                                            <PromoApplianceSingle
                                                appliance={appliance}
                                                key={index}
                                            />
                                        );
                                    })
                            ) : (
                                <p className='no-promo'>
                                    {t('promotions.no-sales-promo')}
                                </p>
                            )}
                        </div>
                        {productWithDiscount &&
                            productWithDiscount.length / APPLIANCE_PER_PAGE >
                                1 && (
                                <Pagination
                                    currentPage={page}
                                    onPageChange={(newPage: number) =>
                                        setPage(newPage)
                                    }
                                    pageNumber={Math.ceil(
                                        productWithDiscount.length /
                                            APPLIANCE_PER_PAGE
                                    )}
                                />
                            )}
                    </div>

                    <div className='redeem-promo section'>
                        <h2>
                            {
                                promoCMS?.attributes?.texte_2?.values?.[0]?.[
                                    currentLocale
                                ]
                            }
                        </h2>
                        <div className='line' />
                        {promoToClaim?.length > 0 ? (
                            <>
                                {promoToClaim.map((promo: any, index: any) => {
                                    return (
                                        <PromoAccessories
                                            key={index}
                                            promoCMS={promo}
                                            allProducts={allProducts}
                                        ></PromoAccessories>
                                    );
                                })}
                            </>
                        ) : (
                            <p className='no-promo'>
                                {t('promotions.no-sales-promo')}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function getPromoToClaim(promotions: any, currentLocale: string) {
    const currentCountry =
        currentLocale === 'en_US' ? 'United States' : 'Canada';
    const promoToClaim = promotions?.filter((promo: any) => {
        const available_countries =
            promo?.attributes?.available_country?.values.map(
                (country: ICurrentLocal) => country['en_CA']
            );
        
        if (available_countries === undefined) {
            return promo;
        } else if (available_countries.includes(currentCountry)) {
            return promo;
        }
    });

    return promoToClaim.filter((e: any) => e.code !== 'drolet_promo-brouillon');
}
